import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';

const S = {
  heading: { fontSize: 26, fontWeight: 300, letterSpacing: '0.06em', color: '#f5f0e8', marginBottom: 4 },
  sub: { fontSize: 13, color: 'rgba(245,240,232,0.35)', marginBottom: 28, letterSpacing: '0.03em' },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, marginBottom: 20 },
  card: { background: '#13110d', border: '1px solid rgba(186,130,69,0.1)', borderRadius: 10, padding: '20px 22px' },
  cardTitle: { fontSize: 11, letterSpacing: '0.35em', color: 'rgba(245,240,232,0.3)', textTransform: 'uppercase', marginBottom: 16 },
};

const BarChart = ({ data, labelKey, valueKey, color = '#ba8245' }) => {
  const max = Math.max(...data.map(d => d[valueKey]), 1);
  return (
    <div>
      {data.map((d, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <div style={{ fontSize: 11, color: 'rgba(245,240,232,0.45)', width: 80, flexShrink: 0, textAlign: 'right' }}>
            {d[labelKey]}
          </div>
          <div style={{ flex: 1, height: 6, background: 'rgba(255,255,255,0.04)', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{
              height: '100%',
              width: `${(d[valueKey] / max) * 100}%`,
              background: color,
              borderRadius: 3,
              transition: 'width 0.6s ease',
              opacity: 0.8,
            }} />
          </div>
          <div style={{ fontSize: 11, color: 'rgba(245,240,232,0.4)', width: 60, flexShrink: 0 }}>
            {d[valueKey]}
          </div>
        </div>
      ))}
    </div>
  );
};

const AdminAnalytics = () => {
  const [data, setData] = useState({
    revenueByMonth: [],
    ordersByStatus: [],
    topProducts: [],
    ordersByDay: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const [ordersRes, itemsRes] = await Promise.all([
        supabase.from('orders').select('status, total_amount, created_at').order('created_at', { ascending: true }),
        supabase.from('order_items').select('product_id, quantity, products(name)'),
      ]);

      const orders = ordersRes.data || [];
      const items = itemsRes.data || [];

      // Revenue by month (last 6 months)
      const monthMap = {};
      const now = new Date();
      for (let i = 5; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const key = d.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
        monthMap[key] = 0;
      }
      orders.forEach(o => {
        if (['paid', 'processing', 'shipped', 'delivered'].includes(o.status)) {
          const d = new Date(o.created_at);
          const key = d.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
          if (monthMap[key] !== undefined) monthMap[key] += parseFloat(o.total_amount || 0);
        }
      });
      const revenueByMonth = Object.entries(monthMap).map(([month, revenue]) => ({ month, revenue: Math.round(revenue) }));

      // Orders by status
      const statusMap = {};
      orders.forEach(o => { statusMap[o.status] = (statusMap[o.status] || 0) + 1; });
      const ordersByStatus = Object.entries(statusMap).map(([status, count]) => ({ status, count })).sort((a, b) => b.count - a.count);

      // Top products by units sold
      const productMap = {};
      items.forEach(item => {
        const name = item.products?.name || `Product #${item.product_id}`;
        productMap[name] = (productMap[name] || 0) + item.quantity;
      });
      const topProducts = Object.entries(productMap)
        .map(([name, units]) => ({ name: name.length > 22 ? name.slice(0, 22) + '…' : name, units }))
        .sort((a, b) => b.units - a.units)
        .slice(0, 7);

      // Orders by day of week
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const dayMap = { Sun: 0, Mon: 0, Tue: 0, Wed: 0, Thu: 0, Fri: 0, Sat: 0 };
      orders.forEach(o => { const d = new Date(o.created_at); dayMap[dayNames[d.getDay()]]++; });
      const ordersByDay = dayNames.map(day => ({ day, orders: dayMap[day] }));

      setData({ revenueByMonth, ordersByStatus, topProducts, ordersByDay });
      setLoading(false);
    };
    load();
  }, []);

  const totalRevenue = data.revenueByMonth.reduce((s, m) => s + m.revenue, 0);

  return (
    <div style={{ color: '#f5f0e8' }}>
      <div style={S.heading}>Analytics</div>
      <div style={S.sub}>Sales insights and store performance overview</div>

      {loading ? (
        <div style={{ color: 'rgba(245,240,232,0.2)', textAlign: 'center', padding: 48 }}>Loading analytics...</div>
      ) : (
        <>
          {/* Summary Row */}
          <div style={{ display: 'flex', gap: 14, marginBottom: 22, flexWrap: 'wrap' }}>
            {[
              { label: '6-Month Revenue', value: `₹${totalRevenue.toLocaleString('en-IN')}`, color: '186,130,69' },
              { label: 'Avg Order Value', value: data.revenueByMonth.some(m => m.revenue > 0) ? `₹${Math.round(totalRevenue / Math.max(data.ordersByStatus.reduce((s, o) => s + o.count, 0), 1)).toLocaleString('en-IN')}` : '₹0', color: '59,130,246' },
              { label: 'Best Month', value: data.revenueByMonth.reduce((best, m) => m.revenue > best.revenue ? m : best, { revenue: 0, month: '—' }).month, color: '34,197,94' },
            ].map(s => (
              <div key={s.label} style={{
                background: '#13110d', border: `1px solid rgba(${s.color},0.15)`,
                borderRadius: 10, padding: '16px 20px',
                borderTop: `2px solid rgba(${s.color},0.5)`,
              }}>
                <div style={{ fontSize: 9, letterSpacing: '0.35em', color: 'rgba(245,240,232,0.3)', textTransform: 'uppercase', marginBottom: 8 }}>{s.label}</div>
                <div style={{ fontSize: 22, fontWeight: 300, color: '#f5f0e8' }}>{s.value}</div>
              </div>
            ))}
          </div>

          <div style={S.grid2}>
            {/* Revenue by Month */}
            <div style={S.card}>
              <div style={S.cardTitle}>Revenue by Month (₹)</div>
              <BarChart data={data.revenueByMonth} labelKey="month" valueKey="revenue" color="#ba8245" />
            </div>

            {/* Orders by Status */}
            <div style={S.card}>
              <div style={S.cardTitle}>Orders by Status</div>
              <BarChart data={data.ordersByStatus} labelKey="status" valueKey="count" color="#93c5fd" />
            </div>

            {/* Top Products */}
            <div style={S.card}>
              <div style={S.cardTitle}>Top Products by Units Sold</div>
              {data.topProducts.length > 0 ? (
                <BarChart data={data.topProducts} labelKey="name" valueKey="units" color="#86efac" />
              ) : (
                <div style={{ color: 'rgba(245,240,232,0.2)', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>No sales data yet</div>
              )}
            </div>

            {/* Orders by Day of Week */}
            <div style={S.card}>
              <div style={S.cardTitle}>Orders by Day of Week</div>
              <BarChart data={data.ordersByDay} labelKey="day" valueKey="orders" color="#c4b5fd" />
            </div>
          </div>

          {/* Order Status Breakdown Donut-style */}
          <div style={{ ...S.card, marginBottom: 20 }}>
            <div style={S.cardTitle}>Order Status Breakdown</div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {data.ordersByStatus.map(({ status, count }) => {
                const colors = {
                  pending: '#fde047', processing: '#93c5fd', shipped: '#c4b5fd',
                  paid: '#86efac', delivered: '#86efac', cancelled: '#fca5a5'
                };
                const total = data.ordersByStatus.reduce((s, o) => s + o.count, 0);
                const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                return (
                  <div key={status} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    background: 'rgba(255,255,255,0.03)', borderRadius: 7,
                    padding: '12px 16px', minWidth: 140,
                  }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: colors[status] || '#888', flexShrink: 0 }} />
                    <div>
                      <div style={{ fontSize: 12, color: 'rgba(245,240,232,0.5)', textTransform: 'capitalize', marginBottom: 2 }}>{status}</div>
                      <div style={{ fontSize: 18, fontWeight: 300, color: '#f5f0e8' }}>{count} <span style={{ fontSize: 11, color: 'rgba(245,240,232,0.3)' }}>({pct}%)</span></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminAnalytics;
